import { GradeSchemeTemplate, RubricTemplate } from '../types';

/** Normalize a name for comparison: lowercase, collapse whitespace, replace en-dash/em-dash with hyphen. */
export function normalizeGradingName(name: string): string {
    return (name || '').toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ').trim();
}

/** Sort templates so items matching the user's region come first. */
export function sortByRegion<T extends { region: string }>(
    items: T[],
    userRegion: string | undefined,
): T[] {
    if (!userRegion) return items;
    const lower = userRegion.toLowerCase();
    return [...items].sort((a, b) => {
        const aMatch = a.region.toLowerCase().includes(lower) ? 0 : 1;
        const bMatch = b.region.toLowerCase().includes(lower) ? 0 : 1;
        return aMatch - bMatch;
    });
}

/** Simple region detection based on browser locale/timezone. */
export function detectUserRegion(): string | undefined {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
        if (tz.startsWith('America/')) return 'United States';
        if (tz.startsWith('Africa/Lagos') || tz.includes('Nigeria')) return 'Nigeria';
        if (tz.startsWith('Africa/Accra') || tz.includes('Ghana')) return 'Ghana';
        if (tz.startsWith('Europe/London') || tz.includes('UK')) return 'United Kingdom';
        if (tz.startsWith('Africa/Johannesburg')) return 'South Africa';
        if (tz.startsWith('Asia/Kolkata') || tz.startsWith('Asia/Calcutta')) return 'India';
        if (tz.startsWith('Australia/')) return 'Australia';
        if (tz.startsWith('America/Toronto') || tz.startsWith('America/Vancouver')) return 'Canada';
    } catch {
        /* ignore */
    }
    return undefined;
}

/** Stable id for grade-scheme entities (new API uses id, legacy uses uuid). */
export function resolveGradeSchemeId(
    scheme: { id?: string | null; uuid?: string | null } | null | undefined,
): string | null {
    if (!scheme) return null;
    return scheme.id ?? scheme.uuid ?? null;
}

/** Stable id for rubric entities. */
export function resolveRubricId(
    rubric: { id?: string | null; uuid?: string | null } | null | undefined,
): string | null {
    if (!rubric) return null;
    return rubric.id ?? rubric.uuid ?? null;
}

export function matchesGradingEntityId(
    item: { id?: string | null; uuid?: string | null } | null | undefined,
    targetId: string | null | undefined,
): boolean {
    if (!item || !targetId) return false;
    return (item.id ?? item.uuid ?? null) === targetId;
}

export const matchesEntityId = matchesGradingEntityId;

export function gradingSummaryLabel(
    rubric: { name?: string; grading_type?: string } | null | undefined,
    scheme: { name?: string } | null | undefined,
): string | null {
    if (!rubric && !scheme) return null;
    const parts: string[] = [];
    if (rubric?.name) parts.push(rubric.name);
    else if (rubric?.grading_type) parts.push(rubric.grading_type.replace(/_/g, ' '));
    if (scheme?.name) parts.push(scheme.name);
    return parts.length ? parts.join(' · ') : null;
}

/** Internal assessment category for points-based rubrics; not shown in the gradebook category band. */
export const POINTS_BASED_ASSESSMENT_CATEGORY = 'Uncategorized';

/** Default category sent to the API for assessment-weighted creates (category is not shown in UI). */
export const ASSESSMENT_WEIGHTED_DEFAULT_CATEGORY = 'General';

export type { GradeSchemeTemplate, RubricTemplate };
